/**
 * デジタルサイネージ詳細ダイアログ用の基底モジュール。
 * @module app/signage/_SignageDialogBase
 */
define([
    'module',
    'dojo/_base/declare',
    'dojo/_base/lang',
    'dojo/date',
    'dojo/date/locale',
    'dojo/dom-style',
    'idis/service/Requester',
    'idis/util/DateUtils',
    'idis/view/Loader',
    'idis/view/page/_PageBase',
    'idis/view/dialog/InfoDialog',
    'app/util/DateFormatter',
    'app/signage/consts/DisplayTypeCode',
    // 以下、変数で受けないモジュール
    'dijit/form/Form',
    'dijit/form/Select',
    'dijit/form/Textarea',
    'dijit/form/TextBox',
    'idis/view/form/AclButton',
    'idis/view/form/Button'
], function (module, declare, lang, date, locale, domStyle, Requester, DateUtils, Loader, _PageBase, InfoDialog,
        DateFormatter, DisplayTypeCode) {

    return declare(module.id.replace(/\//g, '.'), _PageBase,
        {
            // 画面ID
            screenId: null,

            // ディスプレイ種別
            _displayTypeName: null,

            // 被害種別
            _damageType: null,
            // 被害種別リスト
            _damageTypeList: null,

            // バージョン
            _version: null,

            // 最新日時
            // デフォルトは現在日時の直近10分刻み時刻
            latestDateTime: null,

            DAMAGE_TYPE_SELECTED: 'app/signage/damagetype/DamageTypeSelector::selected',
            DAMAGE_TYPE_RELEASED: 'app/signage/damagetype/DamageTypeSelector::released',

            constructor: function () {
                this.latestDateTime = this.roundTime(new Date());
            },

            buildRendering: function () {
                this.inherited(arguments);
            },

            /**
             * フォームをリセットする。
             */
            reset: function () {
                this.form.reset();
            },

            /**
             * 入力値の妥当性を検証する。
             */
            validate: function () {
                var formData = this.form.get('value');

                if (formData.displayName === '') {
                    InfoDialog.show('入力チェック', 'ディスプレイ名称が入力されていません');
                    return false;
                }
                if (formData.updFreq === '') {
                    InfoDialog.show('入力チェック', '更新頻度が入力されていません');
                    return false;
                }
                if (!(String(formData.updFreq).match(/^\d+$/))) {
                    InfoDialog.show('入力チェック', '更新頻度は半角数字で入力してください');
                    return false;
                }
                if (formData.updFreq < 10 || formData.updFreq > 1000) {
                    InfoDialog.show('入力チェック', '更新頻度は10~1000で入力してください');
                    return false;
                }
                if (formData.displayType !== 11 && formData.displayType !== 12 &&
                    formData.displayType !== 13 &&
                    formData.displayType !== 15 && formData.disasterId === '') {
                    InfoDialog.show('入力チェック', '災害が選択されていません');
                    return false;
                }
                if (formData.displayType === 10 && formData.organizationCd === '') {
                    InfoDialog.show('入力チェック', '組織が入力されていません');
                    return false;
                }
                if (formData.displayType === 14 && formData.customizableMunicipalityCd === '') {
                    InfoDialog.show('入力チェック', '表示地域が選択されていません');
                    return false;
                }
                if (formData.startTimestamp !== null && formData.endTimestamp !== null) {
                    if (formData.startTimestamp >= formData.endTimestamp) {
                        InfoDialog.show('入力チェック', '開始と終了を指定する場合、終了日時は開始日時以降を入力してください');
                        return false;
                    }
                }
                return true;
            },

            setDamageType: function (data) {
                this._damageType = data.mainTypeIds[0];
                this._damageTypeList = data.mainTypeIds.join(',');
            },

            releaseDamageType: function () {
                this._damageType = null;
                this._damageTypeList = null;
            },

            /**
             * 災害選択が変更された場合の挙動
             */
            onDisasterChanged: function (disasterId) {
                if (disasterId.value) {
                    domStyle.set(this.disasterTimeStampArea, 'display', '');
                    var promise = Requester.get('/api/disasters/' + disasterId.value);
                    Loader.wait(promise).then(lang.hitch(this, function (data) {
                        if (data.startTimestamp !== null && data.endTimestamp !== null) {
                            this.disasterTimeStamplabel.innerText =
                                DateUtils.format(data.startTimestamp) + ' ～ ' + DateUtils.format(data.endTimestamp);
                        } else if (data.startTimestamp !== null && data.endTimestamp === null) {
                            this.disasterTimeStamplabel.innerText =
                                DateUtils.format(data.startTimestamp) + ' ～ 現在発生中';
                        }
                    }));
                } else {
                    domStyle.set(this.disasterTimeStampArea, 'display', 'none');
                }
            },

            /** 初期表示：検索条件の表示・非表示を管理する*/
            initSearchSelector: function (displayType) {
                this.resetSearchSelector();

                switch (displayType) {
                    case DisplayTypeCode.DISASTER_CORRESPONDENCE:
                        domStyle.set(this.disasterArea, 'display', '');
                        domStyle.set(this.municipalityArea, 'display', '');
                        domStyle.set(this.startTimestampArea, 'display', '');
                        domStyle.set(this.endTimestampArea, 'display', '');
                        break;
                    case DisplayTypeCode.SHELTER_OVERVIEW:
                        domStyle.set(this.disasterArea, 'display', '');
                        domStyle.set(this.startTimestampArea, 'display', '');
                        domStyle.set(this.endTimestampArea, 'display', '');
                        break;
                    case DisplayTypeCode.INDIVIDUAL_SHELTER_LIST:
                        domStyle.set(this.disasterArea, 'display', '');
                        domStyle.set(this.municipalityArea, 'display', '');
                        domStyle.set(this.startTimestampArea, 'display', '');
                        domStyle.set(this.endTimestampArea, 'display', '');
                        domStyle.set(this.shelterStatusArea, 'display', '');
                        break;
                    case DisplayTypeCode.EVACUATION_INFORMATION_OVERVIEW:
                        domStyle.set(this.disasterArea, 'display', '');
                        domStyle.set(this.startTimestampArea, 'display', '');
                        domStyle.set(this.endTimestampArea, 'display', '');
                        break;
                    case DisplayTypeCode.EVACUATION_INFORMATION_HISTORY:
                        domStyle.set(this.disasterArea, 'display', '');
                        domStyle.set(this.municipalityArea, 'display', '');
                        domStyle.set(this.startTimestampArea, 'display', '');
                        domStyle.set(this.endTimestampArea, 'display', '');
                        break;
                    case DisplayTypeCode.DAMAGE_INFORMATION_OVERVIEW:
                        domStyle.set(this.disasterArea, 'display', '');
                        domStyle.set(this.reportedStatusArea, 'display', '');
                        domStyle.set(this.startTimestampArea, 'display', '');
                        domStyle.set(this.endTimestampArea, 'display', '');
                        break;
                    case DisplayTypeCode.INDIVIDUAL_DAMAGE_INFORMATION_LIST:
                        domStyle.set(this.disasterArea, 'display', '');
                        domStyle.set(this.municipalityArea, 'display', '');
                        domStyle.set(this.reportedStatusArea, 'display', '');
                        domStyle.set(this.startTimestampArea, 'display', '');
                        domStyle.set(this.endTimestampArea, 'display', '');
                        break;
                    case DisplayTypeCode.STRUCTURE_OVERVIEW:
                        domStyle.set(this.disasterArea, 'display', '');
                        domStyle.set(this.startTimestampArea, 'display', '');
                        domStyle.set(this.endTimestampArea, 'display', '');
                        break;
                    case DisplayTypeCode.CHRONOLOGY_OVERVIEW:
                        domStyle.set(this.disasterArea, 'display', '');
                        domStyle.set(this.chronologyTypeArea, 'display', '');
                        domStyle.set(this.severityArea, 'display', '');
                        domStyle.set(this.startTimestampArea, 'display', '');
                        domStyle.set(this.endTimestampArea, 'display', '');
                        break;
                    case DisplayTypeCode.INDIVIDUAL_CHRONOLOGY_LIST:
                        domStyle.set(this.disasterArea, 'display', '');
                        domStyle.set(this.chronologyTypeArea, 'display', '');
                        domStyle.set(this.severityArea, 'display', '');
                        domStyle.set(this.municipalityArea, 'display', '');
                        domStyle.set(this.organizationArea, 'display', '');
                        domStyle.set(this.startTimestampArea, 'display', '');
                        domStyle.set(this.endTimestampArea, 'display', '');
                        domStyle.set(this.hldStatusArea, 'display', '');
                        break;
                    case DisplayTypeCode.WEATHER_INFORMATION:
                        break;
                    case DisplayTypeCode.OBSERVATION_OVERVIEW:
                        domStyle.set(this.municipalityArea, 'display', '');
                        break;
                    case DisplayTypeCode.EARTHQUAKE_TSUNAMI_OVERVIEW:
                        domStyle.set(this.startTimestampArea, 'display', '');
                        domStyle.set(this.endTimestampArea, 'display', '');
                        break;
                    case DisplayTypeCode.LIFELINE:
                        domStyle.set(this.disasterArea, 'display', '');
                        domStyle.set(this.customizableMunicipalityArea, 'display', '');
                        break;
                    case DisplayTypeCode.MAP:
                        domStyle.set(this.mapAnnotation, 'display', '');
                        break;
                    case DisplayTypeCode.THEMATIC_EVACUATION:
                        domStyle.set(this.disasterArea, 'display', '');
                        break;
                    default:
                        break;
                }

            },

            resetSearchSelector: function () {
                document.getElementById('organLabel').innerText = '組織選択：';
                domStyle.set(this.organizationArea, 'display', 'none');
                domStyle.set(this.municipalityArea, 'display', 'none');
                domStyle.set(this.disasterArea, 'display', 'none');
                domStyle.set(this.startTimestampArea, 'display', 'none');
                domStyle.set(this.endTimestampArea, 'display', 'none');
                domStyle.set(this.shelterStatusArea, 'display', 'none');
                domStyle.set(this.hldStatusArea, 'display', 'none');
                domStyle.set(this.chronologyTypeArea, 'display', 'none');
                domStyle.set(this.severityArea, 'display', 'none');
                // domStyle.set(this.lifelineArea, 'display', 'none');
                domStyle.set(this.reportedStatusArea, 'display', 'none');
                domStyle.set(this.notifyTypeArea, 'display', 'none');
                domStyle.set(this.damageTypeArea, 'display', 'none');
                domStyle.set(this.notifyDistArea, 'display', 'none');
                domStyle.set(this.riverLevelArea, 'display', 'none');
                domStyle.set(this.earthQuakeArea, 'display', 'none');
                domStyle.set(this.riverTimestampArea, 'display', 'none');
                domStyle.set(this.customizableMunicipalityArea, 'display', 'none');
                domStyle.set(this.mapAnnotation, 'display', 'none');
            },

            // ディスプレイ種別が変更になる場合に呼ぶ
            onDisplayTypeChanged: function () {
                this.initSearchSelector(this.displayType.value);
            },

            /**
             * 直近の10分刻み時刻に丸める
             * 例えば、10時03分の場合は10時00分、10時13分の場合は10時10分に丸められる
             * @param {Date}
             * @returns {Date} 丸めた日時
             */
            roundTime: function (dt) {
                // 分を取得し、10で割り切れなければ、そのあまりでminuteをマイナスする
                var minutes = dt.getMinutes();
                var mod = minutes % 10;
                if (mod !== 0) {
                    return date.add(dt, 'minute', -mod);
                }
                return dt;
            },

            /**
             * 日付のセレクトボックスを生成する
             */
            setDateSelect: function () {
                // 最新日時から1年前までの日付を算出し、セレクトボックスを生成
                var currentDate = this.latestDateTime;
                var counter = 0;
                this._dateOptions = [];
                this._dateOptions.push({ value: '', label: '　' });
                // 選択値。最後に追加されたアクティブなオプション時刻がデフォルト選択値。
                var defaultDate = null;

                // 1年前まで繰り返す
                while (counter < 366) {
                    // セレクトボックスの値
                    var dateValue = locale.format(currentDate, {
                        selector: 'date',
                        datePattern: 'yyyy/MM/dd'
                    });
                    // セレクトボックスのラベル 「yyyy年MM月dd日」
                    var dateLabel = DateFormatter.jpDate(currentDate, true);
                    this._dateOptions.push({ value: dateValue, label: dateLabel });
                    defaultDate = dateValue;

                    // 1日前に更新
                    currentDate = date.add(currentDate, 'day', -1);
                    // カウンターを増やす
                    counter++;
                }
                // セレクトボックスを設定
                this.dateSelect.set('options', this._dateOptions);
                this.dateSelect.startup();

                // デフォルト値の設定
                this.timeSelect.set('value', defaultDate);
            },

            /**
             * 時間のセレクトボックスを生成する
             */
            setTimeSelect: function () {
                // ループの開始時刻（使いたいのは時刻なので日付は何でも良い。0時0分）
                var currentDate = new Date(1970, 1, 1, 0, 0);
                var counter = 0;
                this._timeOptions = [];
                this._timeOptions.push({ value: '', label: '　', disabled: false });
                // 00時00分から23時50分まで生成するので、ループは144回
                while (counter < 144) {
                    // セレクトボックスの値
                    var timeValue = locale.format(currentDate, {
                        selector: 'time',
                        timePattern: 'HH:mm'
                    });
                    // セレクトボックスのラベル
                    var timeLabel = locale.format(currentDate, {
                        selector: 'time',
                        // 24時間指定
                        timePattern: 'HH時mm分'
                    });
                    this._timeOptions.push({ value: timeValue, label: timeLabel, disabled: false });
                    // 10分後に更新
                    currentDate = date.add(currentDate, 'minute', 10);
                    // カウンターを増やす
                    counter++;
                }
                // セレクトボックスを設定
                this.timeSelect.set('options', this._timeOptions);
                this.timeSelect.startup();
            }

        });
});
